import {Controller} from '@hotwired/stimulus';
import Swiper, {Autoplay, Navigation, Pagination, EffectFade} from 'swiper';

export default class extends Controller {
	static get targets () {
		return ['currentCount', 'next', 'prev', 'pagination', 'progressGroup', 'pnWrapper'];
	}

	initialize() {
		this._swiper = null;
		this._shouldAttachEvents = true;

		this._progress = 0;
		this._progressAddition = 0;
		this._progressTimestamp = 0;
		this._config = {};
	}

	connect() {
		this.load();
	}

	disconnect() {
		if (this._swiper) {
			this._swiper.destroy();
			this._swiper = null;
		}

		if (this.onPlanToggleChange) {
			this.element.removeEventListener('plan-toggle:change', this.onPlanToggleChange);
		}

		if (this.onResize) {
			window.removeEventListener('resize', this.onResize);
		}
	}

	equalizePlanHeads() {
		const heads = this.element.querySelectorAll('.plan-card-variant.is-active .plan-head');
		if (!heads.length) return;

		heads.forEach((head) => { head.style.minHeight = ''; });

		const maxHeight = Math.max(...Array.from(heads).map((head) => head.offsetHeight));

		heads.forEach((head) => { head.style.minHeight = `${maxHeight}px`; });
	}
	async load() {
		this._config = this.element.dataset.config ? JSON.parse(this.element.dataset.config) : {};
		const config = this._config;

		config.watchOverflow = true;
		config.observer = true;
		config.observeParents = true;
		config.roundLengths = !this.element.classList.contains('slider-v2');
		if (this.element.classList.contains('slider-v2')) {
			config.spaceBetween = 0;
		}
		if (config.autoplay) {
			Swiper.use([Autoplay]);
			this._progressAddition = 1 / ((config.autoplay.delay + config.speed) / 200);
		}

		if (config.navigation || (this.hasNextTarget && this.hasPrevTarget)) {
			Swiper.use([Navigation]);
			config.navigation = {
				nextEl: this.nextTarget,
				prevEl: this.prevTarget
			};
		}

		if (config.pagination || this.hasPaginationTarget) {
			Swiper.use([Pagination]);
			config.pagination = {
				el: this.paginationTarget
			};
		}

		if (config.effect && config.effect === 'fade') {
			Swiper.use([EffectFade]);
		}

		if (config.fadeEffect && config.fadeEffect.hover) {
			Swiper.use([Autoplay]);
			config.autoplay = {
				delay: 800
			};
			this.element.closest(config.fadeEffect.hover).addEventListener('mouseenter', () => {
				this._swiper.slideNext();
				this._swiper.autoplay.start();
			});

			this.element.closest(config.fadeEffect.hover).addEventListener('mouseleave', () => {
				this._swiper.autoplay.stop();
			});
		}

		if (config.screen) {
			if (window.screen.width < config.screen) {
				delete config.screen;
				this._swiper = new Swiper(this.element, Object.keys(config).length > 0 ? config : undefined);
			}
		} else {
			this._swiper = new Swiper(this.element, Object.keys(config).length > 0 ? config : undefined);
		}

		if (config.fadeEffect && config.fadeEffect.hover) {
			this._swiper.autoplay.stop();
		}

		this.updateCurrent();
		this.equalizePlanHeads();
		this.syncSlideUi();

		this.onPlanToggleChange = () => this.equalizePlanHeads();
		this.element.addEventListener('plan-toggle:change', this.onPlanToggleChange);

		this.onResize = () => this.equalizePlanHeads();
		window.addEventListener('resize', this.onResize);

		if (this._swiper) {
			this._swiper.on('activeIndexChange', () => this.syncSlideUi());
			this._swiper.on('slideChangeTransitionStart', () => this.syncSlideUi());
		}

		if (this.hasProgressGroupTarget) {
			this._swiper.on('slideChange', () => {
				this._progress = 0;
				this.progressGroupTarget.setAttribute('data-slider-progress-group-current-value', this.getCurrent());
				this.progressGroupTarget.setAttribute('data-slider-progress-group-progress-value', this._progress);
				this.syncSlideUi();
				requestAnimationFrame((t) => this.updateProgress(t));
			});

			requestAnimationFrame((t) => this.updateProgress(t));

			window.addEventListener('created-slider-progress-group', () => {
				this.syncSlideUi();
				const btns = this.progressGroupTarget.querySelectorAll('.slider-progress-group__number');
				btns.forEach(btn => {
					btn.addEventListener('click', () => {
						const index = parseInt(btn.dataset.index);
						this._progress = 0;
						this._swiper.slideTo(index);
						this.progressGroupTarget.setAttribute('data-slider-progress-group-current-value', this.getCurrent());
						this.progressGroupTarget.setAttribute('data-slider-progress-group-progress-value', this._progress);
						this.syncSlideUi();
					});
				});
			});
		}

		if (this.hasPnWrapperTarget && this._swiper) {
			this.togglePaginationNavigation();

			this._swiper.on('lock', () => this.togglePaginationNavigation());
			this._swiper.on('unlock', () => this.togglePaginationNavigation());
			this._swiper.on('resize', () => this.togglePaginationNavigation());
			this._swiper.on('breakpoint', () => this.togglePaginationNavigation());

			window.addEventListener('resize', () => {
				requestAnimationFrame(() => this.togglePaginationNavigation());
			});
		}
	}

	allSlidesVisible() {
		if (!this.element.closest('.clubs-block')) {
			return false;
		}

		const swiper = this._swiper;

		if (!swiper || !swiper.slides || !swiper.width) {
			return false;
		}

		const slides = Array.from(swiper.slides).filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));

		if (!slides.length) {
			return true;
		}

		const containerWidth = swiper.el.getBoundingClientRect().width;
		const first = slides[0].getBoundingClientRect();
		const last = slides[slides.length - 1].getBoundingClientRect();

		return (last.right - first.left) <= containerWidth + 1;
	}

	togglePaginationNavigation() {
		if (!this._swiper || !this.hasPnWrapperTarget) {
			return;
		}

		if (this._swiper.isLocked || this.allSlidesVisible()) {
			this.pnWrapperTarget.style.display = 'none';
		} else {
			this.pnWrapperTarget.style.display = null;
		}
	}

	syncSlideUi() {
		if (!this.element.classList.contains('slider-v2')) {
			return;
		}

		let active = null;

		if (this._swiper && this._swiper.slides && this._swiper.slides.length) {
			active = this._swiper.slides[this._swiper.activeIndex];
		}

		if (!active) {
			active = this.element.querySelector('.swiper-slide-active') || this.element.querySelector('.slide');
		}

		if (!active) {
			return;
		}

		const inline = active.style.getPropertyValue('--slide-ui-color')
			|| active.style.getPropertyValue('--slide-title-color');
		const computed = getComputedStyle(active);
		const color = (
			inline
			|| computed.getPropertyValue('--slide-ui-color')
			|| computed.getPropertyValue('--slide-title-color')
			|| '#111111'
		).trim();

		this.element.style.setProperty('--slide-ui-color', color);
	}

	updateCurrent() {
		if (this._swiper && this.hasCurrentCountTarget) {
			let count = this._config.slidesPerView;
			count = count === 'auto' ? 1 : count;

			this.currentCountTargets.forEach(currentCount => {
				if (this._config.breakpoints) {
					for (const [breakpoint, value] of Object.entries(this._config.breakpoints)) {
						if (window.screen.width >= parseInt(breakpoint)) {
							count = value.slidesPerView;
							count = count === 'auto' ? 1 : count;
						}
					}
				}

				count = Math.floor(count);
				count = Math.min(count, this.element.querySelectorAll('.swiper-slide').length);
				currentCount.textContent = count;
			});

			this._swiper.on('slideChange', (swiper) => {
				this.currentCountTargets.forEach(currentCount => {
					currentCount.textContent = count + swiper.realIndex;
				});
			});
		}
	}

	updateProgress(timestamp) {
		if (!this._progressTimestamp) {
			this._progressTimestamp = timestamp;
		}

		const elapsed = timestamp - this._progressTimestamp;

		if (this._swiper.autoplay.running && elapsed >= 200) {
			this._progress += this._progressAddition;
			this.progressGroupTarget.setAttribute('data-slider-progress-group-progress-value', Math.ceil(this._progress * 100));
			this._progressTimestamp = timestamp;
		}

		requestAnimationFrame((t) => this.updateProgress(t));
	}

	getCurrent() {
		return this._swiper.realIndex + 1;
	}
}
